import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { FileUp, X, Camera, ClipboardCheck } from "lucide-react";
import { api, errMsg, mediaUrl } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { InfoPage } from "@/pages/Pages";
import CallbackForm from "@/components/site/CallbackForm";
import { Field, Honeypot, ConsentBox, SubmitBtn, SuccessBox, isEmail } from "@/components/site/FormBits";

const INIT = { tc_no: "", name: "", phone: "", email: "", job_id: "", birth_date: "", city: "", district: "", card_type: "", card_number: "", card_expiry: "", kvkk_ack: false, experience: "", description: "", website: "" };
const MAX = 5 * 1024 * 1024;
const IMG_PDF = /\.(jpe?g|png|pdf)$/i;
const FILES = [
  ["card_front", "Özel Güvenlik Kimlik Kartı Ön Yüzü (JPG, JPEG, PNG, PDF)", IMG_PDF, ".jpg,.jpeg,.png,.pdf", true],
  ["card_back", "Özel Güvenlik Kimlik Kartı Arka Yüzü (JPG, JPEG, PNG, PDF)", IMG_PDF, ".jpg,.jpeg,.png,.pdf", true],
  ["photo", "Fotoğraf (JPG, JPEG, PNG)", /\.(jpe?g|png)$/i, ".jpg,.jpeg,.png"],
  ["cv", "Özgeçmiş (PDF, DOC, DOCX)", /\.(pdf|docx?)$/i, ".pdf,.doc,.docx"],
  ["other", "Diğer Belgeler (PDF, JPG, JPEG, PNG – en fazla 3 dosya)", IMG_PDF, ".jpg,.jpeg,.png,.pdf", false, true],
];
const SUCCESS = "İş başvurunuz başarıyla alınmıştır. Başvurunuzun değerlendirme süreciyle ilgili gelişmeler için iletişim bilgileriniz üzerinden sizinle iletişime geçilecektir.";
const newToken = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);
const fmtDay = (d) => (d ? new Date(`${d}T00:00:00`).toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" }) : "");
const todayIso = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };
export const jobLabel = (j) => [j.title, j.organization, fmtDay(j.date), j.location].filter(Boolean).join(" — ");
const jobMeta = (j) => [j.organization, fmtDay(j.date), j.location].filter(Boolean).join(" · ");

export function validTc(v) {
  if (!/^[1-9]\d{10}$/.test(v)) return false;
  const d = v.split("").map(Number);
  const odd = d[0] + d[2] + d[4] + d[6] + d[8];
  const even = d[1] + d[3] + d[5] + d[7];
  return (odd * 7 - even) % 10 === d[9] && d.slice(0, 10).reduce((a, b) => a + b, 0) % 10 === d[10];
}
const trPhone = (v) => {
  let d = (v || "").replace(/\D/g, "");
  if (d.length > 10) d = d.replace(/^(90|0)/, "");
  return /^[2-5]\d{9}$/.test(d);
};

function age(d) {
  const b = new Date(`${d}T00:00:00`);
  if (Number.isNaN(b.getTime())) return -1;
  const n = new Date();
  return n.getFullYear() - b.getFullYear() - (n < new Date(n.getFullYear(), b.getMonth(), b.getDate()) ? 1 : 0);
}

function fileErr(list, re) {
  for (const f of list) {
    if (f.size > MAX) return `${f.name}: dosya en fazla 5 MB olabilir.`;
    if (!re.test(f.name)) return `${f.name}: bu dosya türü desteklenmiyor.`;
  }
  return undefined;
}

function birthDateError(d) {
  if (!d || d > todayIso()) return "Geçerli bir doğum tarihi seçin (gelecek tarih seçilemez).";
  if (age(d) < 18) return "Başvuru için 18 yaşından büyük olmalısınız.";
  return undefined;
}

function districtError(v) {
  if (v.district) return undefined;
  return v.city ? "İkamet ettiğiniz ilçeyi seçin." : "Önce il seçin.";
}

const validName = (n) => n.trim().length >= 2 && n.trim().includes(" ");

function validatePerson(v, jobs) {
  const checks = {
    tc_no: !validTc(v.tc_no) && "Geçerli bir T.C. kimlik numarası girin (11 hane, yalnızca rakam).",
    name: !validName(v.name) && "Ad ve soyadınızı girin.",
    phone: !trPhone(v.phone) && "Geçerli bir Türkiye telefon numarası girin (örn. 0532 123 45 67).",
    email: !isEmail(v.email) && "Geçerli bir e-posta adresi girin.",
    job_id: !jobs.some((j) => j.id === v.job_id) && "Başvurduğunuz görev veya ilanı seçin.",
    birth_date: birthDateError(v.birth_date),
    city: !v.city && "İkamet ettiğiniz ili seçin.",
    district: districtError(v),
  };
  return Object.fromEntries(Object.entries(checks).filter(([, msg]) => msg));
}

function validateCard(v) {
  const e = {};
  if (!v.card_type) e.card_type = "Kimlik kartı türünü seçin (Silahlı / Silahsız).";
  if (!v.card_number.trim()) e.card_number = "Kimlik kartı numarasını girin.";
  else if (!/^[A-Za-z0-9ÇĞİÖŞÜçğıöşü ./-]{1,30}$/.test(v.card_number.trim())) e.card_number = "Kart numarası yalnızca harf, rakam, boşluk, nokta, tire içerebilir (en fazla 30 karakter).";
  if (!v.card_expiry) e.card_expiry = "Kartın son geçerlilik tarihini seçin.";
  if (!v.kvkk_ack) e.kvkk_ack = "Aydınlatma metnini okuduğunuzu onaylayın.";
  return e;
}

function validateFiles(files) {
  const e = {};
  FILES.forEach(([k, , re]) => { const m = fileErr(files[k] || [], re); if (m) e[k] = m; });
  if (!files.card_front?.length) e.card_front = "Özel güvenlik kimlik kartının ön yüzü yüklenmedi.";
  if (!files.card_back?.length) e.card_back = "Özel güvenlik kimlik kartının arka yüzü yüklenmedi.";
  if ((files.other || []).length > 3) e.other = "En fazla 3 ek belge yükleyebilirsiniz.";
  return e;
}

const validate = (v, files, jobs) => ({ ...validatePerson(v, jobs), ...validateFiles(files), ...validateCard(v) });

function FilePick({ k, label, accept, required, multi, files, setFiles, error }) {
  const list = files[k] || [];
  const pick = (e) => {
    const chosen = Array.from(e.target.files || []);
    if (multi && list.length + chosen.length > 3) toast.error("En fazla 3 ek belge yükleyebilirsiniz.");
    setFiles({ ...files, [k]: multi ? [...list, ...chosen].slice(0, 3) : chosen.slice(0, 1) });
    e.target.value = "";
  };
  return (
    <Field id={`a-${k}`} label={label} required={required} error={error}>
      <div className="flex gap-2">
        <label htmlFor={`a-${k}`} className={`flex min-w-0 flex-1 cursor-pointer items-center gap-3 rounded-md border border-dashed bg-white p-3 text-sm text-zinc-600 transition-colors hover:border-[color:var(--red)] ${error ? "border-red-400" : "border-zinc-300"}`}>
          <FileUp className="h-5 w-5 shrink-0 text-[color:var(--red)]" /> <span className="truncate" data-testid={`apply-file-name-${k}`}>{list.length ? list.map((f) => f.name).join(", ") : "Dosya seçin"}</span>
          {list.length > 0 && <button type="button" onClick={(e) => { e.preventDefault(); setFiles({ ...files, [k]: [] }); }} className="ml-auto text-zinc-400 hover:text-red-600" aria-label="Kaldır" data-testid={`apply-file-clear-${k}`}><X className="h-4 w-4" /></button>}
        </label>
        {required && (
          <label htmlFor={`a-${k}-cam`} className="flex shrink-0 cursor-pointer items-center gap-1.5 rounded-md border border-zinc-300 bg-white px-3 text-sm font-semibold text-zinc-700 hover:border-[color:var(--red)]" title="Kamerayla çek" aria-label="Kamerayla çek" data-testid={`apply-camera-btn-${k}`}>
            <Camera className="h-4 w-4 text-[color:var(--red)]" /> <span>Çek</span>
          </label>
        )}
      </div>
      <input id={`a-${k}`} type="file" multiple={!!multi} accept={accept} className="sr-only" onChange={pick} data-testid={`apply-file-${k}`} />
      {required && <input id={`a-${k}-cam`} type="file" accept="image/*" capture="environment" className="sr-only" onChange={pick} data-testid={`apply-camera-${k}`} />}
    </Field>
  );
}

function JobSelect({ jobs, value, onChange, error }) {
  const job = jobs.find((j) => j.id === value);
  return (
    <div className="space-y-3">
      <Field id="a-job_id" label="Başvurulan Görev / İlan" required error={error}>
        <select id="a-job_id" value={value} onChange={onChange} className="field-input" data-testid="apply-job">
          <option value="">Aktif görev veya ilan seçiniz</option>
          {jobs.map((j) => <option key={j.id} value={j.id}>{jobLabel(j)}</option>)}
        </select>
      </Field>
      {jobs.length === 0 && <p className="text-sm text-zinc-500" data-testid="apply-no-jobs">Şu anda başvuruya açık görev veya ilan bulunmuyor. Aşağıdaki formdan geri arama talebi bırakabilirsiniz.</p>}
      {job && (
        <div className="flex gap-3 rounded-md border-l-4 border-[color:var(--red)] bg-red-50 p-4 text-sm" data-testid="apply-job-summary">
          <ClipboardCheck className="h-5 w-5 shrink-0 text-[color:var(--red)]" />
          <div>
            <p className="font-bold text-zinc-950">{job.title}</p>
            <p className="text-zinc-600">{jobMeta(job)}</p>
            <p className="mt-1 text-xs text-zinc-500">Gönderildikten sonra görev değiştirilemez; farklı bir görev için yeni başvuru yapmanız gerekir.</p>
          </div>
        </div>
      )}
    </div>
  );
}

function LocationFields({ v, setV, locs, errors }) {
  const cities = Object.keys(locs);
  return (
    <div className="grid gap-5 sm:grid-cols-2">
      <Field id="a-city" label="İkametgâh İli" required error={errors.city}>
        <select id="a-city" value={v.city} onChange={(e) => setV({ ...v, city: e.target.value, district: "" })} className="field-input" data-testid="apply-city">
          <option value="">İl seçiniz</option>{cities.map((c) => <option key={c}>{c}</option>)}
        </select>
      </Field>
      <Field id="a-district" label="İkametgâh İlçesi" required error={errors.district}>
        <select id="a-district" value={v.district} disabled={!v.city} onChange={(e) => setV({ ...v, district: e.target.value })} className="field-input disabled:cursor-not-allowed disabled:bg-zinc-100" data-testid="apply-district">
          <option value="">{v.city ? "İlçe seçiniz" : "Önce il seçiniz"}</option>{(locs[v.city] || []).map((d) => <option key={d}>{d}</option>)}
        </select>
      </Field>
    </div>
  );
}

function CardFields({ v, set, errors, input }) {
  return (
    <>
      <Field id="a-card_type" label="Özel Güvenlik Kimlik Kartı Türü" required error={errors.card_type}>
        <div className="flex gap-3" role="radiogroup" data-testid="apply-card_type">
          {[["silahli", "Silahlı"], ["silahsiz", "Silahsız"]].map(([val, l]) => (
            <label key={val} className={`flex flex-1 cursor-pointer items-center justify-center gap-2 rounded-md border p-3 text-sm font-semibold transition-colors ${v.card_type === val ? "border-[color:var(--red)] bg-red-50 text-zinc-950" : "border-zinc-300 text-zinc-700"}`}>
              <input type="radio" name="card_type" value={val} checked={v.card_type === val} onChange={set("card_type")} className="accent-[color:var(--red)]" data-testid={`apply-card-${val}`} /> {l}
            </label>
          ))}
        </div>
      </Field>
      <div className="grid gap-5 sm:grid-cols-2">
        <Field id="a-card_number" label="Özel Güvenlik Kimlik Kartı Numarası" required error={errors.card_number}>{input("card_number", { maxLength: 30 })}</Field>
        <Field id="a-card_expiry" label="Kimlik Kartı Son Geçerlilik Tarihi" required error={errors.card_expiry}>{input("card_expiry", { type: "date" })}</Field>
      </div>
    </>
  );
}

function RegionPicker({ cities, sel, setSel, text, setText }) {
  return (
    <Field id="a-regions" label="Çalışabileceğiniz Şehir veya Bölgeler">
      <select id="a-regions" value="" onChange={(e) => e.target.value && !sel.includes(e.target.value) && setSel([...sel, e.target.value])} className="field-input" data-testid="apply-regions-select">
        <option value="">Şehir ekleyin (birden fazla seçebilirsiniz)</option>{cities.map((c) => <option key={c}>{c}</option>)}
      </select>
      {sel.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2" data-testid="apply-regions-chips">
          {sel.map((c) => <button type="button" key={c} onClick={() => setSel(sel.filter((x) => x !== c))} className="inline-flex items-center gap-1 rounded-full bg-zinc-900 px-3 py-1 text-xs font-semibold text-white">{c} <X className="h-3 w-3" /></button>)}
        </div>
      )}
      <input value={text} onChange={(e) => setText(e.target.value)} maxLength={200} placeholder="Diğer bölgeler (örn. Kuşadası, Söke)" className="field-input mt-2" data-testid="apply-regions-text" />
    </Field>
  );
}

function FormHeader() {
  const { settings: s } = useSite();
  return (
    <div className="flex items-center gap-4 border-b-4 border-[color:var(--red)] pb-4">
      <img src={mediaUrl(s.logo_main) || "/assets/logo-shield.webp"} alt="Rüzgar Özel Güvenlik logosu" className="h-12 w-auto" width="40" height="52" />
      <div>
        <h2 className="text-lg font-extrabold uppercase tracking-tight text-zinc-950 sm:text-xl" data-testid="application-form-title">RÜZGÂR ÖZEL GÜVENLİK – İŞ BAŞVURU FORMU</h2>
        <p className="mt-1 text-xs text-zinc-600 sm:text-sm">İş başvurunuzu eksiksiz doldurarak bize iletebilirsiniz. <span className="font-bold text-[color:var(--red)]">*</span> ile işaretli alanlar zorunludur.</p>
      </div>
    </div>
  );
}

function buildFormData(v, files, regions, token) {
  const fd = new FormData();
  Object.entries(v).forEach(([k, val]) => fd.append(k, String(val)));
  fd.append("regions", regions);
  fd.append("submit_token", token);
  FILES.forEach(([k]) => (files[k] || []).forEach((f) => fd.append(k, f)));
  return fd;
}

function useFormData() {
  const [jobs, setJobs] = useState([]);
  const [locs, setLocs] = useState({});
  useEffect(() => {
    api.get("/public/jobs").then((r) => setJobs(r.data)).catch(() => setJobs([]));
    api.get("/public/locations").then((r) => setLocs(r.data)).catch(() => setLocs({}));
  }, []);
  return { jobs, setJobs, locs };
}

function ApplicationForm() {
  const { jobs, setJobs, locs } = useFormData();
  const [v, setV] = useState(INIT);
  const [files, setFiles] = useState({});
  const [regSel, setRegSel] = useState([]);
  const [regText, setRegText] = useState("");
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const token = useRef(newToken());
  const top = useRef(null);
  const set = (k) => (e) => setV({ ...v, [k]: e?.target ? e.target.value : e });
  useEffect(() => {
    if (done) top.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [done]);

  const submit = async (ev) => {
    ev.preventDefault();
    if (loading) return;
    const e = validate(v, files, jobs);
    setErrors(e);
    if (Object.keys(e).length) {
      toast.error("Lütfen işaretli alanları kontrol edin.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/public/application", buildFormData(v, files, [...regSel, regText.trim()].filter(Boolean).join(", "), token.current));
      setDone(true);
    } catch (err) {
      toast.error(errMsg(err, "Başvurunuz kaydedilemedi. Lütfen tekrar deneyin veya bizi telefonla arayın."));
      if (err.response?.status === 422 && /görev|ilan/i.test(errMsg(err))) api.get("/public/jobs").then((r) => setJobs(r.data));
    } finally {
      setLoading(false);
    }
  };
  if (done) return <div ref={top}><SuccessBox testid="application-success" title="Başvurunuz alındı" text={SUCCESS} /></div>;
  const input = (k, p = {}) => <input id={`a-${k}`} value={v[k]} onChange={set(k)} className="field-input" data-testid={`apply-${k}`} {...p} />;
  return (
    <form onSubmit={submit} noValidate className="relative space-y-5 rounded-lg border border-zinc-200 bg-white p-5 sm:p-7" data-testid="application-form">
      <FormHeader />
      <Honeypot value={v.website} onChange={set("website")} />
      <Field id="a-tc_no" label="T.C. Kimlik Numarası" required error={errors.tc_no}>{input("tc_no", { inputMode: "numeric", maxLength: 11, autoComplete: "off", onChange: (e) => setV({ ...v, tc_no: e.target.value.replace(/\D/g, "").slice(0, 11) }) })}</Field>
      <Field id="a-name" label="Ad Soyad" required error={errors.name}>{input("name", { autoComplete: "name" })}</Field>
      <div className="grid gap-5 sm:grid-cols-2">
        <Field id="a-phone" label="Telefon Numarası" required error={errors.phone}>{input("phone", { type: "tel", inputMode: "tel", placeholder: "05XX XXX XX XX" })}</Field>
        <Field id="a-email" label="E-posta" required error={errors.email}>{input("email", { type: "email" })}</Field>
      </div>
      <JobSelect jobs={jobs} value={v.job_id} onChange={set("job_id")} error={errors.job_id} />
      <Field id="a-birth_date" label="Doğum Tarihi" required error={errors.birth_date}>{input("birth_date", { type: "date", max: todayIso() })}</Field>
      <LocationFields v={v} setV={setV} locs={locs} errors={errors} />
      <CardFields v={v} set={set} errors={errors} input={input} />
      <div className="space-y-4 rounded-md bg-zinc-50 p-4">
        <p className="text-xs text-zinc-500">Her dosya en fazla 5 MB olabilir. Mobil cihazda "Çek" ile kartınızı doğrudan kamerayla çekebilirsiniz. Belgeleriniz herkese açık olmayan güvenli bir alanda saklanır ve yalnızca yetkili yönetici tarafından görüntülenir.</p>
        {FILES.map(([k, label, , accept, required, multi]) => <FilePick key={k} k={k} label={label} accept={accept} required={required} multi={multi} files={files} setFiles={setFiles} error={errors[k]} />)}
      </div>
      <ConsentBox id="apply_kvkk" checked={v.kvkk_ack} onChange={set("kvkk_ack")} error={errors.kvkk_ack} to="/is-basvurusu-aydinlatma-metni" linkText="İş Başvurusu Aydınlatma Metni’ni" rest="okudum. *" />
      <Field id="a-experience" label="Mesleki Deneyim ve Önceki Görevler"><textarea id="a-experience" rows={4} maxLength={2000} value={v.experience} onChange={set("experience")} className="field-input" data-testid="apply-experience" /></Field>
      <RegionPicker cities={Object.keys(locs)} sel={regSel} setSel={setRegSel} text={regText} setText={setRegText} />
      <Field id="a-description" label="Başvuru Açıklaması"><textarea id="a-description" rows={3} maxLength={2000} value={v.description} onChange={set("description")} className="field-input" data-testid="apply-description" /></Field>
      <SubmitBtn loading={loading} testid="apply-submit-btn">BAŞVURUYU GÖNDER</SubmitBtn>
    </form>
  );
}

export default function Careers() {
  return (
    <InfoPage pageKey="kariyer" image="/img/guard.webp">
      <ApplicationForm />
      <div className="mt-8"><CallbackForm defaultInterest="İş başvurusu" /></div>
    </InfoPage>
  );
}
